import { sesionVencida, tokenDeSesion } from "./sesion";

const baseURL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3005";
// 🪤 El nombre quedó de V3 pero apunta a V4 hace rato: el default decía `qa.v3core.blue`, un
// host muerto que en desarrollo hacía fallar todo sin decir por qué.
const v3ApiUrl = process.env.REACT_APP_V3_API_URL || "https://v4.micuenta.blue";

// Además de la cookie va el header `Authorization`, por si la cookie de api.micuenta.blue no
// llegó (ver services/sesion.js). Solo a nuestra API: el token del cliente no sale de ahí.
const conAutorizacion = (url, headers) => {
  if (!String(url).startsWith(baseURL)) return headers;
  const token = tokenDeSesion();
  if (!token) return headers;
  if (typeof Headers !== "undefined" && headers instanceof Headers) {
    const copia = new Headers(headers);
    if (!copia.has("Authorization")) copia.set("Authorization", `Bearer ${token}`);
    return copia;
  }
  // El header explícito de quien llama gana.
  return { Authorization: `Bearer ${token}`, ...(headers || {}) };
};

// El login contesta 401 cuando el DNI está mal: ahí el 401 es la respuesta, no una sesión caída.
const esRutaDeLogin = (url) => /\/api\/(auth|logOut)/.test(String(url));

// Todas las llamadas van con la cookie de sesion. Hasta el 31-8-2026 solo 31 de 86 mandaban
// `credentials: "include"`, asi que la mayoria viajaba anonima: el JWT que emite el login vivia
// en el navegador sin que nadie lo usara. Como la API no miraba la sesion, no se notaba; cuando
// empiece a exigirla (MCB_SESION_MODO), las que no la manden se caen con 401. Esto lo centraliza
// para que no dependa de acordarse en cada fetch nuevo.
const apiFetch = async (url, opciones = {}) => {
  const respuesta = await fetch(url, {
    credentials: "include",
    ...opciones,
    headers: conAutorizacion(url, opciones.headers),
  });

  // 401 de nuestra API = no hay sesión. Sin esto el cliente veía la pantalla roto y no entendía
  // que tenía que volver a entrar; el único rastro quedaba en la consola.
  if (respuesta.status === 401 && String(url).startsWith(baseURL) && !esRutaDeLogin(url)) {
    sesionVencida();
  }

  return respuesta;
};


// --- Compresión de comprobantes (fotos de celular pesan varios MB y a veces no llegan al
// server -> el pago fallaba). Redimensiona imágenes grandes a JPEG liviano; PDFs/chicas quedan igual.
async function compressImageFile(file, maxDim = 1600, quality = 0.7) {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  if (file.size <= 700 * 1024) return file;
  try {
    const dataUrl = await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(file); });
    const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = dataUrl; });
    let { width, height } = img;
    if (width > maxDim || height > maxDim) { const scale = maxDim / Math.max(width, height); width = Math.round(width * scale); height = Math.round(height * scale); }
    const canvas = document.createElement("canvas"); canvas.width = width; canvas.height = height;
    canvas.getContext("2d").drawImage(img, 0, 0, width, height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], (file.name || "comprobante").replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch { return file; }
}
export async function compressReceiptInFormData(formData, field = "archivoAdjunto") {
  try { const f = formData.get(field); if (f && typeof f !== "string") { const c = await compressImageFile(f); if (c !== f) formData.set(field, c); } } catch { /* noop */ }
  return formData;
}



export const verifyTermsAccepted = async (leadId) => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/verifyTermsAccepted/${leadId}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return await response.json();
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const postTerms = async (datos) => {
  try {
    const response = await apiFetch(`${baseURL}/terms`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(datos),
    });
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return await response.json();
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const registrarUsuario = async (datos) => {
  try {
    const response = await apiFetch(`${baseURL}/api/auth/signup`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(datos),
    });
    if (!response.ok) {
      throw new Error("La respuesta de la red no fue exitosa");
    }
    return await response.json();
  } catch (error) {
    console.error("Error al enviar la solicitud:", error);
    throw error; 
  }
};

export const userLogin = async (datos) => {
  try {
    const response = await apiFetch(`${baseURL}/api/auth/signin`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(datos),
    });

    if (!response.ok) {
      throw new Error(
        `Error en la solicitud: ${response.status} ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error al iniciar sesión:", error);
    throw error; 
  }
};

export const userLoginV3 = async (datos) => {
  try {
    const response = await apiFetch(`${baseURL}/api/auth/signinV3`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(datos),
    });

    if (!response.ok) {
      throw new Error(
        `Error en la solicitud: ${response.status} ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error al iniciar sesión:", error);
    throw error;
  }
};

// Impersonación desde el backoffice V4 (token HMAC firmado, sin DNI).
export const backofficeLogin = async (datos) => {
  try {
    const response = await apiFetch(`${baseURL}/api/auth/impersonate`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(datos),
    });

    if (!response.ok) {
      throw new Error(
        `Error en la solicitud: ${response.status} ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error al ingresar desde el backoffice:", error);
    throw error;
  }
};

export const getAllUsersSummary = async (lead_id) => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/getAllUsersSummary/${lead_id}`
    );

    if (!response.ok) {
      throw new Error(
        `Error en la solicitud: ${response.status} ${response.statusText}`
      );
    }

    return await response;
  } catch (error) {
    console.error("Error al obtener el resumen de usuarios:", error);
    throw error; 
  }
};

export const paymentPf = async (formData, customerId) => {
  try {
    await compressReceiptInFormData(formData);
    const response = await apiFetch(`${baseURL}/api/paymentspf/${customerId}`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error(
        `Error en la solicitud: ${response.status} ${response.statusText}`
      );
    }

    return await response;
  } catch (error) {
    console.error("Error al realizar el pago:", error);
    throw error; // Propagar el error para que el llamador pueda manejarlo
  }
};

//  formulario transferencia bancaria

export const paymentTF = async (formData, customerId) => {
  try {
    await compressReceiptInFormData(formData);
    const response = await apiFetch(`${baseURL}/api/payments/${customerId}`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error(
        `Error en la solicitud: ${response.status} ${response.statusText}`
      );
    }

    return await response;
  } catch (error) {
    console.error("Error al realizar el pago:", error);
    throw error; // Propagar el error para que el llamador pueda manejarlo
  }
};

// formulario tarjeta de credito

export const paymentTC = async (formData, customerId) => {
  try {
    await compressReceiptInFormData(formData);
    const response = await apiFetch(`${baseURL}/api/paymentstc/${customerId}`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error(
        `Error en la solicitud: ${response.status} ${response.statusText}`
      );
    }

    return await response;
  } catch (error) {
    console.error("Error al realizar el pago:", error);
    throw error; // Propagar el error para que el llamador pueda manejarlo
  }
};
// formulario tarjeta de debito

export const paymentTD = async (formData, customerId) => {
  try {
    await compressReceiptInFormData(formData);
    const response = await apiFetch(`${baseURL}/api/paymentstd/${customerId}`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error(
        `Error en la solicitud: ${response.status} ${response.statusText}`
      );
    }

    return await response;
  } catch (error) {
    console.error("Error al realizar el pago:", error);
    throw error; // Propagar el error para que el llamador pueda manejarlo
  }
};
// formulario deposito bancario
export const paymentDB = async (formData, customerId) => {
  try {
    await compressReceiptInFormData(formData);
    const response = await apiFetch(`${baseURL}/api/paymentsdb/${customerId}`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error(
        `Error en la solicitud: ${response.status} ${response.statusText}`
      );
    }

    return await response;
  } catch (error) {
    console.error("Error al realizar el pago:", error);
    throw error; // Propagar el error para que el llamador pueda manejarlo
  }
};
// formulario efectivo
export const paymentEF = async (formData, customerId) => {
  try {
    await compressReceiptInFormData(formData);
    const response = await apiFetch(`${baseURL}/api/paymentsef/${customerId}`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error(
        `Error en la solicitud: ${response.status} ${response.statusText}`
      );
    }

    return await response;
  } catch (error) {
    console.error("Error al realizar el pago:", error);
    throw error; // Propagar el error para que el llamador pueda manejarlo
  }
};

export const getPaymentsByOrder = async (customerId, orderId) => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/getPaymentsByOrder/${customerId}/${orderId}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Error en la solicitud: ${response.status} ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error al obtener el historial de pagos:", error);
    throw error;
  }
};

// Error de la API que conserva el status y el texto que mandó el backend. Sin esto, un
// rechazo con motivo (por ejemplo el candado de selección: "ya registraste un pago") llegaba
// a la pantalla como un número suelto y el usuario veía un mensaje fijo que no correspondía.
const errorDeApi = async (response, contexto) => {
  let msg = "";
  try {
    const body = await response.json();
    msg = (body && (body.message || body.error)) || "";
  } catch (_) {
    // sin cuerpo JSON: queda el status pelado
  }
  const err = new Error(msg || `${contexto}: ${response.status}`);
  err.status = response.status;
  err.serverMessage = msg;
  return err;
};

// Guardar items
// cambiada a async

export const saveItems = async (customerId, selectedItems) => {
  try {
    const response = await apiFetch(`${baseURL}/api/saveItems/${customerId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(selectedItems),
    });
    if (!response.ok) {
      throw await errorDeApi(response, "Error al guardar items");
    }
    return await response.json();
  } catch (error) {
    if (error && error.status) throw error;
    throw new Error(`Error al guardar items: ${error}`);
  }
};

// logout
export const logOut = async () => {
  try {
    const response = await apiFetch(`${baseURL}/api/logOut`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      throw new Error(
        `Error al cerrar sesión: ${response.status} ${response.statusText}`
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Error al cerrar sesión:", error);
    throw error; // Propagar el error para que el llamador pueda manejarlo
  }
};

//getitems
export const getItems = async (order_id) => {
  try {
    const response = await apiFetch(`${baseURL}/api/getItems/${order_id}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      throw new Error(
        `Error fetching items: ${response.status} ${response.statusText}`
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching items:", error);
    throw error; // Propagar el error para que el llamador pueda manejarlo
  }
};

export const getPaymentInformation = async (customerId, orderId) => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/getPaymentInformation/${customerId}/${orderId}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    if (!response.ok) {
      throw new Error(
        `Error fetching payment information: ${response.status} ${response.statusText}`
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching payment information:", error);
    throw error; // Propagar el error para que el llamador pueda manejarlo
  }
};

export const getUserSizes = async (customerId, orderItemId) => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/getUserSizes/${customerId}/${orderItemId}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    if (!response.ok) {
      throw new Error(
        `Error fetching payment information: ${response.status} ${response.statusText}`
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching payment information:", error);
    throw error; // Propagar el error para que el llamador pueda manejarlo
  }
};

// api.js
export const getAgentInfo = async (leadId) => {
  if (!leadId) {
    throw new Error("Lead ID is required");
  }
  try {
    const response = await apiFetch(`${baseURL}/api/getAgentInfo/${leadId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });
    if (!response.ok) {
      throw new Error(
        `Error fetching agent info: ${response.status} ${response.statusText}`
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching agent info:", error);
    throw error; // Propagar el error para que el llamador pueda manejarlo
  }
};

// paymentMethod
export const getPaymentMethodsByOrderId = async (order_id) => {
  try {
    const response = await apiFetch(`${baseURL}/api/getPaymentMethods/${order_id}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      throw new Error(response.status);
    }
    return await response.json();
  } catch (error) {
    throw new Error(`Error en la solicitud de métodos de pago: ${error}`);
  }
};

// storePayments
export const storePayments = async (
  customerId,
  selectedPaymentSelectionId,
  order_id
) => {
  try {
    const response = await apiFetch(`${baseURL}/api/storePayments`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        customer_id: customerId,
        payment_selection_id: selectedPaymentSelectionId,
        order_id: order_id,
      }),
    });
    if (!response.ok) {
      throw await errorDeApi(response, "Error en la petición de almacenar pagos");
    }
    return await response.json();
  } catch (error) {
    if (error && error.status) throw error;
    throw new Error(`Error en la petición de almacenar pagos: ${error}`);
  }
};

//get feeInfo
export const getFeeInfo = async (id) => {
  try {
    const response = await apiFetch(`${baseURL}/api/getFeeInfo/${id}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Error en la solicitud");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

// cambiada a async

export const getDesignExtra = async (leadId) => {
  const response = await apiFetch(`${baseURL}/api/getDesignExtra/${leadId}`, {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    throw new Error("Error en la solicitud del costo extra");
  }
  return await response.json();
};

//usuarios con deudas
export const getUserDebts = async (customer_id, order_id) => {
  try {
    const response = await apiFetch(`${baseURL}/api/getAccountSummary/${customer_id}/${order_id}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Error en la solicitud de deudas");
    }
    return await response.json();
  } catch (error) {
    console.error("Error al obtener las deudas del usuario:", error);
    throw error; // Lanza el error para manejarlo en el componente
  }
};

//get extra por cantidad
export const getExtraLowerQuantity = async (leadId) => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/getExtraLowerQuantity/${leadId}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        "Error en la solicitud del costo extra por menor cantidad de alumnos"
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error al obtener el costo extra:", error);
    throw error; // Lanza el error para manejarlo en el componente
  }
};

//Pregunta si el customer eligio alguna prenda en el pedido activo:
export const hasCustomerItemsSelected = async (customer_id, order_id) => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/hasCustomerItemSelected/${customer_id}/${order_id}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        "Error en la solicitud del costo extra por menor cantidad de alumnos"
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error al obtener el costo extra:", error);
    throw error; // Lanza el error para manejarlo en el componente
  }
};

//Tabla de estado de cuenta (fetch)
// Link de pago GetNet que coincide con la selección de prendas del cliente (tarjeta de crédito).
export const getGetnetLink = async (customer_id, order_id) => {
  try {
    const response = await apiFetch(`${baseURL}/api/getGetnetLink/${customer_id}/${order_id}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) throw new Error("No se pudo obtener el link de pago");
    return await response.json();
  } catch (error) {
    throw error;
  }
};

// Opciones de retiro habilitadas según la provincia del colegio (Little Blue / Little Santiago).
export const getDeliveryOptions = async (order_id) => {
  try {
    const response = await apiFetch(`${baseURL}/api/getDeliveryOptions/${order_id}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) throw new Error("No se pudieron obtener las opciones de entrega");
    return await response.json();
  } catch (error) {
    throw error;
  }
};

// Estado de cuentas de toda la orden con las columnas del Exportar/Imprimir del
// backoffice (selección, forma de pago, vencidas, adeudado, última vencida, total, pagado).
export const fetchAccountsExport = async (order_id) => {
  try {
    const response = await apiFetch(`${baseURL}/api/getAccountsExport/${order_id}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) throw new Error("No se pudo obtener el estado de cuentas");
    return await response.json();
  } catch (error) {
    throw error;
  }
};

export const fetchCustomersSummary = async (order_id, page) => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/getAllUsersSummary/${order_id}?page=${page}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        "Error en la solicitud del costo extra por menor cantidad de alumnos"
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error al obtener el costo extra:", error);
    throw error;
  }
};

export const fetchFeesWithInterest = async (userDebts) => {
  try {
    const response = await apiFetch(`${baseURL}/api/getCompoundInterest`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ userDebts }),
    });

    if (!response.ok) {
      throw new Error("Error en la respuesta del servidor");
    }

    const data = await response.json();
    return data; // Asume que el servidor devuelve las cuotas actualizadas con intereses
  } catch (error) {
    throw error;
  }
};
// cargar talles

export const uploadSize = async (data) => {
  try {
    const response = await apiFetch(`${baseURL}/api/uploadSize`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error("Error al crear el talle");
    }

    const jsonResponse = await response.json();
    return jsonResponse;
  } catch (error) {
    throw new Error(`Error al enviar la petición: ${error.message}`);
  }
};

// llamar a todos los usuarios
export const prendasCadaUsuario = async (leadId, orderItemId) => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/fetchAllUsersOrderItem/${leadId}/${orderItemId}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching guide:", error);
    throw error;
  }
};

export const fetchAllUsers = async (leadId) => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/getAllUsersFromOrder/${leadId}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return await response.json();
    // Suponiendo que esperas un arreglo de usuarios en la respuesta
  } catch (error) {
    console.error("Error al obtener usuarios:", error);
    throw error;
  }
};
// getsize
export const getSizes = async (orderItemId) => {
  try {
    const response = await apiFetch(`${baseURL}/api/getSizes/${orderItemId}`);
    if (!response.ok) {
      throw new Error("Failed to fetch sizes");
    }
    return await response.json();
  } catch (error) {
    throw error;
  }
};

// Progreso de talles del cliente en la orden (total vs cargados) → para el inicio.
export const getSizesProgress = async (customerId, orderId) => {
  try {
    const response = await apiFetch(`${baseURL}/api/getSizesProgress/${customerId}/${orderId}`);
    if (!response.ok) {
      throw new Error("Failed to fetch sizes progress");
    }
    return await response.json();
  } catch (error) {
    throw error;
  }
};

// delete size
export const deleteSize = async (id) => {
  try {
    const response = await apiFetch(`${baseURL}/api/deleteSize/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Failed to delete size");
    }

    return id; // Devuelve el ID eliminado si es necesario
  } catch (error) {
    throw error;
  }
};

// guias
export const getPrendasTutor = async (leadId, orderId = null, accessCode = null) => {
  try {
    const params = new URLSearchParams();
    if (orderId) params.append("order_id", orderId);
    if (accessCode) params.append("access_code", accessCode);
    const qs = params.toString() ? `?${params.toString()}` : "";
    const response = await apiFetch(
      `${baseURL}/api/getAllItemsForTutor/${leadId}${qs}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching items for tutor:", error);
    throw error;
  }
};

export const getAllCategories = async (customer_id, order_id) => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/getAllCategories/${customer_id}/${order_id}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching categories:", error);
    throw error;
  }
};

export const getGuide = async (orderItemId) => {
  try {
    const response = await apiFetch(`${baseURL}/api/getGuide/${orderItemId}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching guide:", error);
    throw error;
  }
};

export const getSketchByOrderItemId = async (orderItemId) => {
  try {
    const response = await apiFetch(`${baseURL}/api/getSketch/${orderItemId}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (response.status === 404) {
      return {};
    }
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching sketch:", error);
    throw error;
  }
};

export const approveSketch = async (orderItemId, customerId, sketchId) => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/approveSketch/${orderItemId}/${sketchId}`,
      {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ customerId }),
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching sketch:", error);
    throw error;
  }
};

export const rejectSketch = async (formData, orderItemId, sketchId) => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/rejectSketch/${orderItemId}/${sketchId}`,
      {
        method: "POST",
        credentials: "include",
        body: formData,
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching sketch:", error);
    throw error;
  }
};

export const getAditionalDataOfItem = async (orderItemId) => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/getAditionalDataOfItem/${orderItemId}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching sketch:", error);
    throw error;
  }
};

export const saveAddress = async (formData) => {
  try {
    const response = await apiFetch(`${baseURL}/api/saveAddress`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    });

    if (!response.ok) {
      throw new Error(
        `Error en la solicitud: ${response.status} ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error al enviar el formulario:", error);
    throw error; // Propagar el error para que el llamador pueda manejarlo
  }
};

export const getAllProvinces = async () => {
  try {
    const response = await apiFetch(`${baseURL}/api/getAllProvinces`);

    if (!response.ok) {
      throw new Error(
        `Error en la solicitud: ${response.status} ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error al cargar provincias:", error);
    throw error; // Propagar el error para que el llamador pueda manejarlo
  }
};
export const getDepartments = async (provinciaSeleccionada) => {
  return await apiFetch(`${baseURL}/api/getDepartments/${provinciaSeleccionada}`)
    .then((response) => response.json())
    .catch((error) => console.error("Error al cargar municipios:", error));
};

export const getDistricts = async (municipioSeleccionado) => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/getDistricts/${municipioSeleccionado}`
    );

    if (!response.ok) {
      throw new Error(
        `Error en la solicitud: ${response.status} ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error al cargar localidades:", error);
    throw error; // Propagar el error para que el llamador pueda manejarlo
  }
};

export const uploadOrderItemGuide = async (formData, orderItemId) => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/uploadOrderItemGuide/${orderItemId}`,
      {
        method: "POST",
        credentials: "include",
        body: formData,
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return await response.json();
  } catch (error) {
    console.error("Error uploading guide:", error);
    throw error;
  }
};

export const obtainGuideStatus = async (ordetItemId) => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/getCurrentGuideInfo/${ordetItemId}`
    );

    if (!response.ok) {
      throw new Error(
        `Error en la solicitud: ${response.status} ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error getting guide info status:", error);
    throw error; // Propagar el error para que el llamador pueda manejarlo
  }
};

export const getCurrentAddress = async (order_id) => {
  try {
    const response = await apiFetch(`${baseURL}/api/getCurrentAddress/${order_id}`);
    if (!response.ok) {
      throw new Error(`Error ${response.status} - ${response.statusText}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error al obtener la dirección actual:", error);
    throw error;
  }
};

export const getSelectedDirection = async (order_id) => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/getSelectedDirection/${order_id}`
    );
    if (!response.ok) {
      throw new Error(
        `Error en la solicitud: ${response.status} ${response.statusText}`
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Error getting selected directions:", error);
    throw error;
  }
};

export const getProvinceById = async (id) => {
  try {
    const response = await apiFetch(`${baseURL}/api/getProvinceById/${id}`);

    if (!response.ok) {
      throw new Error(
        `Error en la solicitud: ${response.status} ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error getting province:", error);
    throw error; // Propagar el error para que el llamador pueda manejarlo
  }
};

export const getDepartmentById = async (id) => {
  try {
    const response = await apiFetch(`${baseURL}/api/getDepartmentById/${id}`);

    if (!response.ok) {
      throw new Error(
        `Error en la solicitud: ${response.status} ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error getting department:", error);
    throw error; // Propagar el error para que el llamador pueda manejarlo
  }
};

export const getDistrictById = async (id) => {
  try {
    const response = await apiFetch(`${baseURL}/api/getDistrictById/${id}`);

    if (!response.ok) {
      throw new Error(
        `Error en la solicitud: ${response.status} ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error al obtener el distrito:", error);
    throw error; // Propagar el error para que el llamador pueda manejarlo
  }
};

export const getSizeById = async (id) => {
  try {
    const response = await apiFetch(`${baseURL}/api/getSizeById/${id}`);

    if (!response.ok) {
      throw new Error(
        `Error en la solicitud: ${response.status} ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error al obtener la información del tamaño:", error);
    throw error; // Propagar el error para que el llamador pueda manejarlo
  }
};

export const editSize = async (data, id) => {
  try {
    const response = await apiFetch(`${baseURL}/api/editSize/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error("Error al editar el talle");
    }

    const jsonResponse = await response.json();
    return jsonResponse;
  } catch (error) {
    throw new Error(`Error al enviar la petición: ${error.message}`);
  }
};

export const completeSizes = (datos) => {
  return apiFetch(`${baseURL}/api/completeSizes`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(datos),
  });
};

export const sendPickUpAtOffice = (datos) => {
  return apiFetch(`${baseURL}/api/setPickUpAtOffice`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(datos),
  });
};

export const getOrderItemStatus = async (leadId, orderItemIds) => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/getOrderItemStatus/${leadId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          orderItemIds: orderItemIds,
        }),
      }
    );
    if (!response.ok) {
      throw new Error("No se pudo obtener el estado del pedido");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error al obtener el estado del pedido:", error);
    throw error;
  }
};

export const getOrderItemsExtras = async (customer_id) => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/getOrderItemsExtras/${customer_id}`
    );

    if (!response.ok) {
      throw new Error(
        `Error en la solicitud: ${response.status} ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error al obtener los extras de los order_items:", error);
    throw error; // Propagar el error para que el llamador pueda manejarlo
  }
};

// fecha de expiracion de beneficios por metodos de pago

export const getExpirationDate = async (orderId) => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/retornarExpiracionDeBeneficios/${orderId}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Error fetching expiration date: ${response.status} ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching expiration date:", error);
    throw error;
  }
};

// rifas

export const validarRifa = async (codigo, number) => {
  // Valida contra MCB (antes pegaba a V3, que quedó congelado en el cutover).
  try {
    const response = await apiFetch(
      `${baseURL}/api/raffles/check-existance/${encodeURIComponent(codigo)}/${encodeURIComponent(number)}`,
      { method: "GET", headers: { "Content-Type": "application/json" } }
    );

    const data = await response.json();

    if (!response.ok || data.status !== 0) {
      throw new Error(data.message || "Código y/o número de rifa incorrecto");
    }

    return data; // Devuelve la respuesta si es exitosa
  } catch (error) {
    throw error; // Lanza el error para manejarlo en el componente
  }
};

// campos de la rifa

export const fillRaffleData = async (code, number, bodyData) => {
  const url = `${baseURL}/api/raffles/fillData/${code}/${number}`;

  try {
    const response = await apiFetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(bodyData), // Convert the form data to JSON format
    });

    if (!response.ok) {
      throw new Error("Error al enviar los datos. Verifique la información.");
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error en la solicitud POST:", error);
    throw error;
  }
};

export const getAllPrizes = async () => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/raffles/getPrizeImages`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching categories:", error);
    throw error;
  }
};

/**
 * Retornar los talles por id de orden
 * @param {*} orderId 
 */
export const getSizesByOrderId = async (orderId, moldingId, orderItemCategoryId, moldingName) => {
  try {
    const queryParams = new URLSearchParams();

    if (moldingId) queryParams.append("moldingId", moldingId);
    // Por NOMBRE cuando no hay id: desde «Cargar talle» y «Editar talle» el front sólo conoce el
    // nombre de la moldería. Sin mandar una de las dos, la API no puede caer al catálogo.
    if (!moldingId && moldingName) queryParams.append("moldingName", moldingName);
    if (orderItemCategoryId) queryParams.append("orderItemCategoryId", orderItemCategoryId);
    
    const response = await apiFetch(
      `${baseURL}/api/sizes/${orderId}?${queryParams.toString()}`
    );

    if (!response.ok) {
      throw new Error("Error en la solicitud");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

//Formulario público de carga de talles
export const fetchFormSizesData = async (orderId) => {
  try {
    const response = await apiFetch(
      `${v3ApiUrl}/api/public/select-sizes/${orderId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching form sizes data:', error);
    throw error;
  }
};

//Guardar talles del formulario publico
export const saveFormSizesData = async (data) => {
  try {
    const response = await apiFetch(
      `${v3ApiUrl}/api/public/select-sizes`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching form sizes data:', error);
    throw error;
  }
}

//Talles faltantes de una orden (para exportar)
export const fetchMissingSizes = async (orderId) => {
  try {
    const response = await apiFetch(
      `${v3ApiUrl}/api/public/missing-sizes/${orderId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching form sizes data:', error);
    throw error;
  }
};

//Validar y login del tutor o delegado para el formulario de talles
export const loginTutor = async (dni, customers) => {
  const res = await apiFetch(
    `${baseURL}/api/auth/liable-login`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ dni, customers }),
    }
  );

  const data = await res.json();

  return {
    ok: res.ok,
    data,
  };
};

//Validar si el customer ha sido desactivado
export const validateCustomerActive = async (customerId) => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/isCustomerAlive/${customerId}`,
      { method: "GET" }
    );

    if (!response.ok) {
      throw new Error(
        `Error en la solicitud: ${response.status} ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error al validar el estado del cliente:", error);
    throw error;
  }
};

export const getAvailableOrders = async (customerId) => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/orders/available/${customerId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching available orders:", error);
    throw error;
  }
};

export const getMasterTreeByOrderItemId = async (orderItemId, $b, productModelId = null) => {
  try {
    const params = new URLSearchParams({
      order_item_id: String(orderItemId),
      b: String($b),
    });
    if (productModelId) {
      params.append("product_model_id", String(productModelId));
    }
    const response = await apiFetch(
      `${v3ApiUrl}/api/design/datasheet/create?${params.toString()}`,
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching master tree:", error);
    throw error;
  }
};

export const getIfGarmentIsReversible = async (orderItemId, productModelId = null) => {
  try {
    const params = new URLSearchParams({
      order_item_id: String(orderItemId),
    });
    if (productModelId) {
      params.append("product_model_id", String(productModelId));
    }
    const response = await apiFetch(
      `${v3ApiUrl}/api/design/datasheet/is-reversible?${params.toString()}`,
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching reversible master tree:", error);
    throw error;
  }
};

export const getDesignerVariants = async (orderItemId) => {
  try {
    const response = await apiFetch(
      `${v3ApiUrl}/api/design/datasheet/variants?order_item_id=${encodeURIComponent(orderItemId)}`
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching designer variants:", error);
    throw error;
  }
};

export const getAvailableColors = async (materialId) => {
  try {
    const response = await apiFetch(
      `${v3ApiUrl}/api/design/datasheet/create/get-available-colors/${materialId}`
    );

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching available colors:", error);
    throw error;
  }
};

export const getFullprints = async () => {
  const response = await apiFetch(`${v3ApiUrl}/api/design/fullprints`);
  if (!response.ok) throw new Error("Error obteniendo fullprints");
  return response.json();
};

export const getAllColors = async () => {
  const response = await apiFetch(`${v3ApiUrl}/api/design/datasheet/colors`);
  if (!response.ok) throw new Error("Error obteniendo colores");
  return response.json();
};

export const getPrintsDetails = async (printIds = []) => {
  if (!printIds.length) return { prints: [] };
  const params = printIds.map(id => `id[]=${encodeURIComponent(id)}`).join("&");
  const response = await apiFetch(`${v3ApiUrl}/api/design/prints?${params}`);
  if (!response.ok) throw new Error("Error obteniendo prints");
  return response.json();
};

export const getLittlePredesigns = async () => {
  const response = await apiFetch(`${v3ApiUrl}/api/design/prints/little`);
  if (!response.ok) throw new Error("Error obteniendo prediseños Little");
  return response.json();
};

// Tipografías administradas desde el ABM de V4 (/typographies). Devuelve
// { fonts: [{ id, legacy_id, name, family, file, file_url, scale, for }] }.
export const getDesignFonts = async () => {
  const response = await apiFetch(`${v3ApiUrl}/api/design/fonts`);
  if (!response.ok) throw new Error("Error obteniendo tipografías");
  return response.json();
};

export const getMasterTemplateForOrderItem = async (orderItemId, b = null) => {
  const params = new URLSearchParams({ order_item_id: String(orderItemId) });
  if (b !== null && b !== undefined && b !== "") {
    params.set("b", String(b));
  }
  const response = await apiFetch(
    `${v3ApiUrl}/api/design/datasheet/exists?${params.toString()}`
  );
  if (!response.ok) throw new Error("Error consultando ficha");
  return response.json();
};

export const deleteMasterTemplate = async (datasheetId) => {
  const response = await apiFetch(
    `${v3ApiUrl}/api/design/datasheet/${datasheetId}`,
    { method: "DELETE" }
  );
  if (!response.ok) throw new Error("Error eliminando ficha");
  return response.json();
};

export const saveMasterTemplate = async (payload) => {
  try {
    const response = await apiFetch(`${v3ApiUrl}/api/design/datasheet/store`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(
        `Error en la solicitud: ${response.status} ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error saving master template:", error);
    throw error;
  }
};

export const submitDesignerBundle = async (orderItemId, payload) => {
  try {
    const response = await apiFetch(
      `${baseURL}/api/designer/submit/${encodeURIComponent(orderItemId)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload || {}),
      }
    );
    if (!response.ok) {
      throw new Error(`Error ${response.status} ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error submitting designer bundle:", error);
    throw error;
  }
};

export const finalizeDesignerArtifacts = async (payload) => {
  try {
    const response = await apiFetch(`${v3ApiUrl}/api/design/datasheet/artifacts/finalize`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload || {}),
    });
    if (!response.ok) {
      throw new Error(`Error ${response.status} ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error finalizing designer artifacts:", error);
    throw error;
  }
};
